import { ArrowLineUp } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { type BoxProps } from "@tokenterminal/ui/Box"
import { ButtonV3 as Button } from "@tokenterminal/ui/Button/Button.v3"
import { Card } from "@tokenterminal/ui/Card"
import { Divider } from "@tokenterminal/ui/Divider"
import { useDisclosure } from "@tokenterminal/ui/hooks"
import { Row } from "@tokenterminal/ui/Row"
import {
  type ButtonHTMLAttributes,
  type ReactNode,
  useCallback,
  useId,
} from "react"
import {
  collapsableToggleCss,
  collapsableWidgetContentCss,
  collapsableWidgetCss,
  collapsableWidgetHeaderCss,
} from "./CollapsableWidget.css"

type CollapseableCardProps = {
  children: ReactNode
  direction?: "left" | "right" | "top" | "bottom"
  isDefaultOpen?: boolean
  className?: string
  hideToggleButton?: boolean
  hideDivider?: boolean
  header:
    | ReactNode
    | ((args: { isExpanded: boolean; toggleExpanse: () => void }) => ReactNode)
}

function CollapseToggle({
  isExpanded,
  onToggle,
  ...props
}: {
  isExpanded: boolean
  onToggle: (isExpanded: boolean) => void
  className: BoxProps["className"]
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color" | "className">) {
  return (
    <Button
      onClick={(e) => {
        e.stopPropagation()

        onToggle(!isExpanded)
      }}
      display={["none", "flex"]}
      aria-expanded={isExpanded}
      size="square"
      {...props}
    >
      <Icon as={ArrowLineUp} width={12} height={12} />
    </Button>
  )
}

export function CollapsableWidget({
  direction = "top",
  isDefaultOpen,
  className,
  children,
  header,
  hideToggleButton,
  hideDivider,
  ...props
}: CollapseableCardProps) {
  const {
    isOpen: isExpanded,
    toggle: toggleExpanse,
    open,
  } = useDisclosure(isDefaultOpen) // Default open in desktop. Maybe should use global atom
  const controlId = useId()

  const onClick = useCallback(() => {
    if (!isExpanded) {
      open()
    }
  }, [isExpanded, open])

  const alignment =
    direction === "top" || direction === "bottom" ? "vertical" : "horizontal"

  return (
    <Card
      {...props}
      data-expanded={isExpanded}
      data-alignment={alignment}
      data-direction={direction}
      className={[collapsableWidgetCss, className]}
      onClick={onClick}
      paddingX="4x"
      paddingTop="4x"
      paddingBottom="none"
    >
      <div className="flex flex-col gap-4 h-full max-w-full">
        <Row as="header" gap="s" className={collapsableWidgetHeaderCss}>
          {typeof header === "function"
            ? header({ isExpanded, toggleExpanse })
            : header}
          {!hideToggleButton && (
            <CollapseToggle
              isExpanded={isExpanded}
              onToggle={toggleExpanse}
              aria-expanded={isExpanded}
              aria-controls={controlId}
              className={collapsableToggleCss}
            />
          )}
        </Row>
        <Divider bleedX="s" opacity={hideDivider ? 0 : isExpanded ? 1 : 0} />
        <div
          className={`flex flex-col gap-6 pb-4 ${collapsableWidgetContentCss}`}
          id={controlId}
          aria-hidden={!isExpanded}
        >
          {children}
        </div>
      </div>
    </Card>
  )
}
